import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, Loader2, BarChart3, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Sparkline } from "@/components/Sparkline";
import { getExchange } from "@/data/exchanges";
import { fmtUsd, fmtPct, pnlColor } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function PartnerPortfolio() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["partner-pf-accounts"],
    queryFn: async () => (await api.get("/partner/portfolio/accounts")).data,
  });

  const filtered = accounts
    .filter((a) => (statusFilter ? a.status === statusFilter : true))
    .filter((a) => (q.trim() ? a.account_name?.toLowerCase().includes(q.toLowerCase()) : true));

  return (
    <div>
      <PageHeader title="Portfolio Tracker" pageKey="portfolio" testid="partner-portfolio-header" />

      <div className="flex items-center gap-2 mb-5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid="pf-search"
            className="w-64 bg-[#0A0A0A] border border-[#333333] rounded-md pl-9 pr-3 py-2 text-sm text-[#F5F5F5] placeholder:text-[#525252] focus:outline-none focus:border-[#D4AF37]" />
        </div>
        <button onClick={() => setStatusFilter((s) => (s === "active" ? "" : "active"))} data-testid="pf-filter"
          className={cn("w-9 h-9 rounded-md border flex items-center justify-center transition-colors",
            statusFilter ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#333333] text-[#A3A3A3] hover:text-[#F5F5F5]")}>
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#333333] bg-[#0D0D0D] p-16 text-center">
          <UsersIcon className="w-8 h-8 text-[#525252] mx-auto mb-3" />
          <div className="text-[#F5F5F5] font-medium">No client portfolios</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a, i) => {
            const ex = getExchange(a.exchange_slug);
            const m = a.metrics || {};
            return (
              <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-lg border border-[#1f1f1f] bg-[#0D0D0D] p-5 flex flex-col" data-testid={`pf-card-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#333333] flex items-center justify-center"><UsersIcon className="w-4 h-4 text-[#737373]" /></div>
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#0D0D0D] border-2 border-[#0D0D0D] flex items-center justify-center font-heading font-bold text-[8px]" style={{ color: ex.color }}>{ex.name.slice(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-[#F5F5F5] truncate">{a.account_name}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[9px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-1.5 py-0.5 rounded-sm">{a.connected ? "Connected" : "Off"}</span>
                      <span className="text-[9px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-1.5 py-0.5 rounded-sm capitalize">{a.market}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-[#737373]">PNL</span>
                  <span className={cn("font-mono text-sm font-semibold", pnlColor(m.total_return))}>{m.total_return != null ? fmtPct(m.total_return) : "0%"}</span>
                </div>
                <div className="h-[48px] mt-1"><Sparkline series={m.pnl_series} height={44} /></div>

                <div className="grid grid-cols-2 gap-y-3 gap-x-4 mt-3">
                  <div>
                    <div className="font-mono text-base font-semibold text-[#F5F5F5]">{m.total_return != null ? fmtPct(m.total_return) : "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Total Return</div>
                  </div>
                  <div>
                    <div className="font-mono text-base font-semibold text-[#F5F5F5]">{a.balance != null ? fmtUsd(a.balance) : "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Balance</div>
                  </div>
                  <div>
                    <div className="font-mono text-sm text-[#A3A3A3]">{m.active_days ?? "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Active Days</div>
                  </div>
                  <div>
                    <div className="font-mono text-sm text-[#EF4444]">{m.drawdown != null ? `${m.drawdown}%` : "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-[#737373]">Drawdown</div>
                  </div>
                </div>

                <button onClick={() => navigate(`/partner/portfolio/${a.id}`)} data-testid={`pf-stats-btn-${a.id}`}
                  className="mt-4 flex items-center justify-center gap-2 border border-[#262626] rounded-md py-2.5 text-sm text-[#D4AF37] hover:bg-[rgba(212,175,55,0.06)] transition-colors">
                  <BarChart3 className="w-4 h-4" /> Stats and Reports
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
